import { useCurrentUnit } from '../hooks/useCurrentUnit.js'

// UnitSwitcher — header control for multi-unit owners to scope the app
// to one unit or the All-Units roll-up.
//
// CORE REGRESSION GUARANTEE: renders NOTHING for single-unit users
// (!isGrouped). They never see a switcher, so their experience is
// byte-identical to before multi-unit existed.
//
// A native <select> keyed by a sentinel value for the roll-up:
//   '__all_units__' → switchToAllUnits()
//   <restaurant id> → switchUnit(id)
// The pinned "All Units (Roll-Up)" option sits ABOVE the alphabetical
// unit list. The trigger shows a primary-color dot when a specific unit
// is selected (i.e. NOT the roll-up) so the active scope reads at a
// glance against the amber header.
const ALL_UNITS_VALUE = '__all_units__'

export default function UnitSwitcher() {
  // Hook called unconditionally, before any early return (React #310).
  const { mode, isGrouped, allUnits, currentRestaurantId, switchUnit, switchToAllUnits } =
    useCurrentUnit()

  if (!isGrouped) return null

  const selectValue = mode === 'all_units' ? ALL_UNITS_VALUE : (currentRestaurantId ?? '')

  function onChange(e) {
    const v = e.target.value
    if (v === ALL_UNITS_VALUE) switchToAllUnits()
    else if (v) switchUnit(v)
  }

  return (
    <label className="relative flex items-center">
      <span className="sr-only">Active unit</span>
      {/* Primary-color dot — only when a specific unit is in scope. */}
      {mode === 'group' && (
        <span
          aria-hidden="true"
          className="pointer-events-none absolute left-2 h-1.5 w-1.5 rounded-full bg-amber-brand-600"
        />
      )}
      <select
        value={selectValue}
        onChange={onChange}
        className={`
          max-w-[10rem] truncate rounded-md bg-amber-brand-200/40
          py-0.5 pr-5 text-[11px] font-medium text-amber-brand-800
          hover:bg-amber-brand-200/60 focus:outline-none
          ${mode === 'group' ? 'pl-5' : 'pl-2'}
        `}
        title="Switch unit"
      >
        <option value={ALL_UNITS_VALUE}>All Units (Roll-Up)</option>
        {allUnits.map((u) => (
          <option key={u.id} value={u.id}>{u.name}</option>
        ))}
      </select>
    </label>
  )
}
